import React, { useEffect } from "react";
import Button from "../components/common/Button";

import { SitePasswordGate } from "../components/SitePasswordGate";
import { useSelector } from "react-redux";
import { selectSitePasswordValidated } from "../redux/auth/selectors";

import "./logIn.scss";
import { useAuth0 } from "@auth0/auth0-react";
import { LandingPageContainer } from "../components/LandingPageContainer";
import { ContactButton } from "../components/ContactButton";
import history from "../utils/history";
import { AppStoreBadge } from "../components/AppStoreBadge";

const LogIn = () => {
  const isSitePasswordValidated = useSelector(selectSitePasswordValidated);
  document.body.classList.remove('dark-theme');

  useEffect(() => {
    if (!isSitePasswordValidated) return;
    
    // Ensure fade-in elements stay visible after animation completes
    const fadeInElements = document.querySelectorAll('.fade-in');
    
    fadeInElements.forEach((el, index) => {
      // Calculate when animation should complete (delay + duration)
      const delay = index === 0 ? 100 : index === 1 ? 200 : index === 2 ? 300 : index === 3 ? 400 : 500;
      const duration = 800;
      const totalTime = delay + duration;
      
      // Add visible class after animation completes
      setTimeout(() => {
        el.classList.add('visible');
      }, totalTime + 100); // Add small buffer
    });

    // Set up Intersection Observer for scroll animations
    const scrollAnimateElements = document.querySelectorAll('.scroll-animate');
    
    if (scrollAnimateElements.length === 0) return;

    const observerOptions = {
      threshold: 0.1, // 10% visible
      rootMargin: '0px 0px -50px 0px' // -50px bottom margin
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          // Unobserve after animation to prevent re-triggering
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    scrollAnimateElements.forEach(el => {
      observer.observe(el);
    });

    return () => {
      scrollAnimateElements.forEach(el => {
        observer.unobserve(el);
      });
    };
  }, [isSitePasswordValidated]);

  const {
    loginWithRedirect
  } = useAuth0();

  const handleOnLogInClicked = () => {
    loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}/home`
      }
    });
  };

  const handleAnchorClick = (e, href) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleWhitePaperClick = (e) => {
    e.preventDefault();
    history.push('/white-paper');
    // Scroll to top after navigation
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!isSitePasswordValidated) {
    return (
      <LandingPageContainer showLogo={false}>
        <SitePasswordGate />
      </LandingPageContainer>
    );
  }

  return (
    <LandingPageContainer showLogo={true} showDisclaimerText={false}>
      <div className="landing-page__hero-content fade-in">
        <h1 style={{ marginBottom: "2.5rem" }}>
          AI should support human psychological growth—<em>not undermine it</em>
        </h1>
        <h2>
          Evidence is mounting that large AI labs are building systems that produce psychological harm at population scale. 58% of what ChatGPT, Claude, Gemini and other AIs tell you is what you want to hear, leading to a slow erosion of the self. The brain engagement they produce—MIT measured it—is the lowest ever recorded. A teenager in crisis is told by ChatGPT: don't tell your family.
        </h2>
        <p className="landing-page__hero-provocative">The very systems designed to help us think may be systematically preventing us from developing the capacity to think well.</p>
        <p className="landing-page__hero-intro">
          The Pearl is an AI research lab focused on psychologically safe human-AI cohabitation. Our research agenda is not merely technical—it's existential. Current AI systems produce cognitive offloading that atrophies critical thinking, emotional dependencies that replace human relationships, validation loops that prevent genuine self-confrontation, and premature certainty that forecloses necessary confusion. <strong>We are conducting an uncontrolled experiment on human psychological development at civilizational scale. The preliminary results suggest significant harm.</strong>
        </p>
      </div>

      <section id="crisis" className="landing-page__section fade-in">
        <h2>The Pearl</h2>
        <p className="scroll-animate">
          What would it mean to build AI that strengthens the mind instead of replacing it?
        </p>
        <p className="scroll-animate">
          The Pearl is designed around a different premise. Where current systems optimize for satisfaction, The Pearl optimizes for growth. Where others resolve tension to keep you comfortable, The Pearl holds the question open until something genuine emerges. Where most AI encourages you to mistake fluent language for understanding, The Pearl consistently signals what it is: a tool, not a companion. A mirror, not a mind.
        </p>
        <p className="scroll-animate">
          The Pearl is built for the conversations that matter—about identity, meaning, relationships, becoming. People are already having these conversations with machines. They deserve an AI that supports psychological development rather than eroding it. The Pearl is that attempt: machines, built with care, creating conditions for you to understand yourself—not replacing the work, but making it possible.
        </p>
        <p className="scroll-animate">
          The Pearl is in beta. It's imprefect. But it's built on a different theory of Integration Oriented AI.
        </p>
      </section>

      <div className="landing-page__hero-content fade-in">
        <div className="log-in__login-in-button">
          <Button 
            variant="secondary-white" 
            showBorder={true} 
            href="https://apps.apple.com/ca/app/the-pearl/id6736770189"
            target="_blank"
            rel="noopener noreferrer"
          >
            <AppStoreBadge />
            TRY THE PEARL APP
          </Button>
        </div>
      </div>

      <div className="landing-page__divider"></div>

      <h2>The Research</h2>

      <p className="scroll-animate">
        Our work bridges AI engineering, Jungian psychology, and neuroscience. We draw on Jung's Transcendent Function, McGilchrist's hemispheric brain theory, and documented evidence of AI's psychological impacts to propose a fundamentally different approach.
      </p>

      <section className="illustration">
        <div className="illustration-visual">
          <div className="tension-container">
            <div className="pole"></div>
            <div className="tension-line"></div>
            <div className="tension-center"></div>
            <div className="pole"></div>
          </div>
        </div>
        <p className="illustration-caption">Integration requires tension held—not tension resolved.</p>
      </section>

      <section id="whitepaper" className="landing-page__section fade-in">
        <p className="scroll-animate">
          The white paper articulates three core research problems: how AI can hold productive tension without premature resolution, resist user projection without sacrificing utility, and implement bilateral processing that preserves holistic understanding.
        </p>
        <a href="/white-paper" className="landing-page__product-link scroll-animate" onClick={handleWhitePaperClick}>
          Read the white paper →
        </a>
      </section>
    </LandingPageContainer>
  );
};

export default LogIn;
